import cx from 'classnames';
import { Icon, Link, TooltipPortalTrigger, TooltipTrigger } from 'components';
import FormFieldType from 'constants/FormFieldType';
import withLaunchDarkly from 'hocs/withLaunchDarkly';
import PhoneNumber from 'lib/PhoneNumber';
import { capitalize, formatPhoneNumber } from 'lib/utils';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import './style.scss';

const ignoredFieldTypes = [FormFieldType.Divider];

class FormSummary extends Component {
  static propTypes = {
    title: PropTypes.string,
    itemName: PropTypes.string,
    fields: PropTypes.array,
    values: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    'data-cy': PropTypes.string
  };

  static defaultProps = {
    fields: {},
    values: {}
  };

  getValue(field, values) {
    if (!field) {
      return undefined;
    }

    const value = values[field.id];

    switch (field.type) {
      case FormFieldType.Checkbox:
        return value ? 'Yes' : 'No';
      case FormFieldType.TextBox: {
        if (!field.options.hasCheckbox) {
          return undefined;
        }

        return value === true ? 'Yes' : value === false ? 'No' : '';
      }

      case FormFieldType.DatePicker:
        return value ? moment(value).format('MMM Do, YYYY') : '—';

      case FormFieldType.Select: {
        const { items = [], type } = field.options;

        if (type === 'radio') {
          const item = items.find(i => i.value === value);
          return item ? item.label : undefined;
        }

        if (type === 'checkbox') {
          return items
            .filter(i => (value || []).includes(i.value))
            .map(i => i.label)
            .join(', ');
        }

        break;
      }

      case FormFieldType.FileUpload: {
        if (!Array.isArray(value) || value.length === 0) return undefined;
        return (
          <ul className="form-summary__item-value__list">
            {value.map(file => (
              <li className={cx('form-summary__item-value__list-item', 'form-summary__item-value__list-item--file')}>
                <Link to={file.file_url} target="_blank">
                  {file.name}
                </Link>
              </li>
            ))}
          </ul>
        );
      }

      case FormFieldType.TextInput:
      default:
        return value;
    }
  }

  getDataArray(fields, values) {
    const filteredFields = fields.filter(
      f =>
        Object.prototype.hasOwnProperty.call(values, f.id) &&
        (!ignoredFieldTypes.includes(f.type) || (f.type === FormFieldType.TextBox && f.options.hasCheckbox))
    );

    const mappedFields = filteredFields.map(f => {
      let customFormat = f.customAttributeOptions?.format;

      // Backward compatibility for mobile_phone field, which should have the customFormat set by backend
      if (!customFormat && f.id === 'mobile_phone') {
        customFormat = 'phone';
      }

      return {
        id: f.id,
        label: f.options.label,
        value: this.getValue(f, values),
        multiline: f.options.multiline,
        format: customFormat
      };
    });

    return mappedFields;
  }

  renderItems() {
    const { fields, values, itemName, flags } = this.props;
    const enablePhoneNumberFormatting = flags['phone-number-validation'];

    if (!Array.isArray(values)) {
      return this.getDataArray(fields, values)
        .map(this.renderItem)
        .filter(item => item !== null);
    }

    return values
      .map(v => this.getDataArray(fields, v))
      .map((v, index) => (
        <React.Fragment key={itemName + index}>
          {values.length > 1 && (
            <div className="form-summary__item-header">
              {capitalize(itemName)} #{index + 1}
            </div>
          )}

          {v.map((i, index) => this.renderItem(i, index, enablePhoneNumberFormatting))}
        </React.Fragment>
      ));
  }

  renderItem(i, index, enablePhoneNumberFormatting) {
    if (i.value === '' || i.value === undefined) {
      return null;
    }

    let showPhoneValidationError = false;
    if (i.format === 'phone') {
      showPhoneValidationError = enablePhoneNumberFormatting
        ? i.value && !PhoneNumber.fromString(i.value).isValid()
        : false;
    }

    return (
      <div key={i.id || `item_${index}`} className={cx('form-summary__item', `form-summary__item--${i.id}`)}>
        <div className="form-summary__item-label">{i.label}</div>

        <div
          className={cx('form-summary__item-value', {
            'form-summary__item-value--multiline': i.multiline
          })}
        >
          <div>{i.format === 'phone' ? formatPhoneNumber(i.value || '', enablePhoneNumberFormatting) : i.value}</div>

          {showPhoneValidationError && (
            <div className="alert-right phone-number--invalid">
              <TooltipPortalTrigger tooltip="Please enter a valid phone number" side="center">
                <Icon name="alert" size="18" className="form-input__invalid-icon" />
              </TooltipPortalTrigger>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { title, 'data-cy': dataCY } = this.props;

    const renderedItems = this.renderItems();

    if (!renderedItems.length) return null;

    return (
      <div className="form-summary" data-cy={dataCY}>
        <div className="form-summary__title">{title}</div>

        <div className="form-summary__items">{renderedItems}</div>
      </div>
    );
  }
}

export default withLaunchDarkly(FormSummary);
